import React, { useState } from 'react';
import { Play, ExternalLink } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const ProjectCard = ({ project }) => {
  const [activeVideo, setActiveVideo] = useState(project.videoUrl);
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div 
      className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl transition-all duration-500 hover:shadow-3xl hover:-translate-y-2 w-full"
      style={{ backgroundColor: project.bgColor }}
    >
      {/* Video Section */}
      {project.videoUrl && (
        <div className="aspect-video relative overflow-hidden bg-black w-full">
          {isPlaying ? (
            <iframe
              src={`${activeVideo}?autoplay=1`}
              title={project.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div 
              className="relative w-full h-full cursor-pointer"
              onClick={() => setIsPlaying(true)}
            >
              <img 
                src={project.thumbnail} 
                alt={project.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 sm:w-20 h-16 sm:h-20 bg-terracotta rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                  <Play className="w-8 sm:w-10 h-8 sm:h-10 text-white ml-1" />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No Video - Show Carousel or Single Image */}
      {!project.videoUrl && (
        <div className="w-full" style={{ aspectRatio: '16/9', position: 'relative', overflow: 'hidden', background: '#000' }}>
          {project.images && project.images.length > 1 ? (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
              <Swiper
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={0}
                slidesPerView={1}
                navigation={true}
                pagination={{ 
                  clickable: true,
                  dynamicBullets: false
                }}
                autoplay={{ 
                  delay: 4000, 
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true
                }}
                loop={true}
                style={{ width: '100%', height: '100%' }}
              >
                {project.images.map((img, idx) => (
                  <SwiperSlide key={idx}>
                    <div style={{ width: '100%', height: '100%' }}>
                      <img 
                        src={img} 
                        alt={`${project.title} - Image ${idx + 1}`}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          ) : (
            <img 
              src={project.thumbnail} 
              alt={project.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
        </div>
      )}

      {/* Project Details */}
      <div className="p-4 sm:p-6 lg:p-8 w-full" style={{ color: project.textColor }}>
        <div className="flex flex-col sm:flex-row items-start justify-between mb-3 gap-2">
          <div className="flex-1">
            <p className="text-xs opacity-70 mb-1 uppercase tracking-wider font-body">{project.client}</p>
            <h4 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold mb-2">
              {project.title}
            </h4>
          </div>
          <span 
            className="border-2 border-current px-3 py-1 rounded-lg text-xs font-body uppercase tracking-wider"
          >
            {project.year}
          </span>
        </div>

        <p className="opacity-80 mb-4 text-sm sm:text-base leading-relaxed font-body">{project.description}</p>

        <div className="mb-4">
          <p className="text-xs font-semibold mb-2 opacity-60 uppercase tracking-widest font-body">ROLE</p>
          <p className="text-sm font-semibold font-body">{project.role}</p>
        </div>

        {/* Reach Metrics */}
        {project.reach && project.reach.length > 0 && (
          <div className="mb-6 pb-6 border-b-2 border-current/20">
            <p className="text-xs font-semibold mb-4 opacity-60 uppercase tracking-widest font-body">REACH</p>
            <div className="grid grid-cols-3 gap-4">
              {project.reach.map((metric, i) => (
                <div key={i} className={`text-center ${metric.fullWidth ? 'col-span-3' : ''}`}>
                  <p className="text-xl sm:text-2xl font-display font-black mb-1">{metric.value}</p>
                  <p className="text-xs opacity-70 font-body">{metric.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Episodes */}
        {project.episodes && project.episodes.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold mb-4 opacity-60 uppercase tracking-widest font-body">FEATURED EPISODES</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {project.episodes.map((episode, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActiveVideo(episode.videoUrl);
                    setIsPlaying(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-left p-3 rounded-xl bg-black/20 hover:bg-black/30 transition-all border border-current/10"
                >
                  <p className="text-xs font-semibold mb-1 font-body">{episode.title}</p>
                  <p className="text-xs opacity-70 font-body">{episode.views} views</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* PSA Videos */}
        {project.psaVideos && project.psaVideos.length > 0 && (
          <div className="mb-6">
            <p className="text-xs font-semibold mb-4 opacity-60 uppercase tracking-widest font-body">PSA VIDEOS</p>
            <div className="space-y-2">
              {project.psaVideos.map((psa, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActiveVideo(psa.videoUrl);
                    setIsPlaying(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="w-full text-left p-3 rounded-xl bg-black/20 hover:bg-black/30 transition-all flex items-center justify-between border border-current/10"
                >
                  <p className="text-xs font-semibold font-body pr-2 flex-1">{psa.title}</p>
                  <ExternalLink className="w-4 h-4 opacity-50 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-4">
          {project.tags.map((tag, i) => (
            <span 
              key={i} 
              className="text-xs px-3 py-2 rounded-xl bg-black/20 border border-current/20 font-body uppercase tracking-wider"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;
